import { Box, useMediaQuery, Menu, MenuItem, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOutUser } from '../../api';
import Icon from '../../components/icon';
import { H5 } from '../../components/typography';
import { auth, db } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { COLORS } from '../../theme';
import { AccountCircle, KeyboardArrowDown } from '@mui/icons-material';
import HyriaLogo from '../../assets/hyria-logo.svg';
import { checkUserHasAssociationApartments } from '../../api/housing';
import NoAssociationModal from '../../components/NoAssociationModal';
import ConsentModal from '../../components/modals/ConsentModal';

const Header = () => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showNoAssociationModal, setShowNoAssociationModal] = useState(false);
  const [noAssociationPendingClaim, setNoAssociationPendingClaim] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    auth.onAuthStateChanged(async (user) => {
      setIsUserLoggedIn(!!user);
      if (user) {
        setUserEmail(user.email || '');
        setIsLoadingUserData(true);
        
        // Hämta användardata från Firestore för att få det riktiga namnet
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Använd fullName först, sedan firstName + lastName, sedan email som fallback
            if (userData.fullName) {
              setUserName(userData.fullName);
            } else if (userData.firstName && userData.lastName) {
              setUserName(`${userData.firstName} ${userData.lastName}`);
            } else if (userData.firstName) {
              setUserName(userData.firstName);
            } else {
              setUserName(user.email?.split('@')[0] || 'Användare');
            }
          } else {
            // Om ingen Firestore-data finns, använd email som fallback
            setUserName(user.email?.split('@')[0] || 'Användare');
          }
        } catch (error) {
          setUserName(user.email?.split('@')[0] || 'Användare');
        } finally {
          setIsLoadingUserData(false);
        }
      } else {
        setUserName('');
        setIsLoadingUserData(false);
      }
    });
  }, []);

  // Scroll detection for sticky header with throttling and buffer zone
  useEffect(() => {
    let ticking = false;
    let lastScrollPosition = window.scrollY;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;

          // Add hysteresis (buffer zone) to prevent rapid toggling
          // When scrolling down, require 30px before switching to scrolled state
          // When scrolling up, require dropping below 10px before switching back
          if (scrollPosition > lastScrollPosition) {
            // Scrolling down
            if (scrollPosition > 30 && !isScrolled) {
              setIsScrolled(true);
            }
          } else {
            // Scrolling up
            if (scrollPosition < 10 && isScrolled) {
              setIsScrolled(false);
            }
          }

          lastScrollPosition = scrollPosition;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'), {
    noSsr: true
  });

  const handleSignOut = async () => {
    const res = await signOutUser();
    if (!res) {
      alert('Kunde inte logga ut. Vänligen försök igen.');
      return;
    }
    window.localStorage.clear();
    handleMenuClose();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Funktion för att hantera "Skapa ansökan" klick
  const handleCreateApplication = () => {
    if (!auth.currentUser?.uid) {
      navigate('/signin');
      return;
    }

    // Visa samtyckes-modal direkt (ingen fördröjning)
    setShowConsentModal(true);
  };

  // Hantera när användaren godkänner villkoren
  const handleConsentAccept = async () => {
    setShowConsentModal(false);

    // Gör association-kontrollen EFTER samtycke
    try {
      const associationCheck = await checkUserHasAssociationApartments(auth.currentUser.uid);

      if (!associationCheck.hasAssociationApartments) {
        setNoAssociationPendingClaim(Boolean(associationCheck.hasPendingClaim));
        setShowNoAssociationModal(true);
        return;
      }

      navigate('/create');
    } catch (error) {
      // Om kontrollen misslyckas, låt användaren gå till create-sidan ändå
      navigate('/create');
    }
  };

  const handleProfileClick = () => {
    navigate('/');
    handleMenuClose();
  };

  const getUserDisplayName = () => {
    // Visa loading state medan vi hämtar användardata
    if (isLoadingUserData) {
      return 'Laddar...';
    }
    
    // Använd det riktiga namnet från Firestore om det finns
    if (userName) {
      return userName;
    }
    
    // Fallback till email-prefix
    if (userEmail) {
      return userEmail.split('@')[0];
    }
    
    return 'Användare';
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        width: '100vw',
        zIndex: 1100,
        backgroundColor: isScrolled
          ? 'rgba(255,255,255,0.75)'
          : 'rgba(255,255,255,0.9)',
        backdropFilter: isScrolled
          ? 'saturate(180%) blur(12px)'
          : 'saturate(180%) blur(8px)',
        borderBottom: isScrolled
          ? '1px solid rgba(0,0,0,0.08)'
          : '1px solid rgba(0,0,0,0.06)',
        boxShadow: isScrolled
          ? '0 2px 16px rgba(0,0,0,0.08)'
          : '0 1px 8px rgba(0,0,0,0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: isScrolled ? (isMobile ? 56 : 64) : (isMobile ? 70 : 80),
        willChange: 'height, background-color, box-shadow, backdrop-filter'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile 
            ? (isScrolled ? '8px 16px' : '16px 20px')
            : (isScrolled ? '8px 24px' : '16px 32px'),
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          height: '100%'
        }}
      >
      <Box
        onClick={() => navigate('/applications')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <img 
          src={HyriaLogo} 
          alt="Hyria" 
          style={{ 
            height: isMobile 
              ? (isScrolled ? 28 : 32) 
              : (isScrolled ? 32 : 40), 
            width: 'auto',
            filter: 'invert(1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }} 
        />
      </Box>

      {isUserLoggedIn && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isScrolled ? 3 : 4,
            transition: 'gap 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {/* Dokument Link */}
            <Box
              onClick={() => navigate('/document')}
              sx={{ 
                cursor: 'pointer',
                position: 'relative',
                padding: '8px 4px',
                display: 'flex',
                alignItems: 'center',
                minHeight: '36px',
                transition: 'all 0.2s ease, opacity 0.2s ease',
                opacity: 1,
                '&:hover': {
                  opacity: 0.8,
                  transform: 'translateY(-1px)',
                  '& .underline': {
                    width: '100%',
                    opacity: 1
                  }
                }
              }}
            >
              <H5
                color={COLORS.black}
                fontWeight="500"
                sx={{ 
                  margin: 0,
                  lineHeight: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: COLORS.primary
                  }
                }}
              >
                {t('documents')}
              </H5>
              <Box
                className="underline"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  height: 2,
                  width: location.pathname === '/document' ? '100%' : '0%',
                  backgroundColor: COLORS.primary,
                  borderRadius: 1,
                  transition: 'all 0.3s ease',
                  opacity: location.pathname === '/document' ? 1 : 0
                }}
              />
            </Box>

            {/* Min sida Link */}
            <Box
              onClick={() => navigate('/applications')}
              sx={{ 
                cursor: 'pointer',
                position: 'relative',
                padding: '8px 4px',
                display: 'flex',
                alignItems: 'center',
                minHeight: '36px',
                transition: 'all 0.2s ease, opacity 0.2s ease',
                opacity: 1,
                '&:hover': {
                  opacity: 0.8,
                  transform: 'translateY(-1px)',
                  '& .underline': {
                    width: '100%',
                    opacity: 1
                  }
                }
              }}
            >
              <H5
                color={COLORS.black}
                fontWeight="500"
                sx={{ 
                  margin: 0,
                  lineHeight: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: COLORS.primary
                  }
                }}
              >
                Min sida
              </H5>
              <Box
                className="underline"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  height: 2,
                  width: (location.pathname === '/applications' || location.pathname.startsWith('/applications/')) ? '100%' : '0%',
                  backgroundColor: COLORS.primary,
                  borderRadius: 1,
                  transition: 'all 0.3s ease',
                  opacity: (location.pathname === '/applications' || location.pathname.startsWith('/applications/')) ? 1 : 0
                }}
              />
            </Box>

            {/* Skapa ansökan Button */}
            <Box
              onClick={handleCreateApplication}
              sx={{ 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(74, 159, 209, 1)',
                borderRadius: '6px',
                padding: '8px 12px',
                minHeight: '36px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(64, 149, 199, 1)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(74, 159, 209, 0.3)'
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  background: 'rgba(54, 139, 189, 1)'
                }
              }}
            >
              <H5
                sx={{ 
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  letterSpacing: '0.3px',
                  lineHeight: 1,
                  margin: 0
                }}
              >
                Skapa ansökan
              </H5>
            </Box>
            
            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleMenuOpen}
                sx={{ 
                  color: COLORS.black,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                <AccountCircle sx={{ fontSize: 24 }} />
                <H5 fontWeight="600" sx={{ textTransform: 'none' }}>
                  {getUserDisplayName()}
                </H5>
                <KeyboardArrowDown sx={{ fontSize: 20, color: COLORS.black, opacity: 0.7 }} />
              </IconButton>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    borderRadius: 2,
                    mt: 1,
                    minWidth: 160,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <MenuItem 
                  onClick={handleProfileClick}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(63, 81, 181, 0.04)'
                    }
                  }}
                >
                  <H5 fontWeight="600" color={COLORS.black}>
                    {t('My profile')}
                  </H5>
                </MenuItem>
                <MenuItem 
                  onClick={handleSignOut}
                  sx={{ 
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(63, 81, 181, 0.04)'
                    }
                  }}
                >
                  <H5 fontWeight="600" color={COLORS.black}>
                    {t('Logout')}
                  </H5>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* No Association Modal */}
      <NoAssociationModal
        isOpen={showNoAssociationModal}
        hasPendingClaim={noAssociationPendingClaim}
        onClose={() => setShowNoAssociationModal(false)}
      />

      {/* Consent Modal */}
      <ConsentModal
        open={showConsentModal}
        onClose={() => setShowConsentModal(false)}
        onAccept={handleConsentAccept}
      />
      </Box>
    </Box>
  );
};

export default Header;
